/**
 * Line items off a receipt: tap an item, tap who shared it.
 *
 * An item with nobody assigned falls into the group cost, which is exactly how a shared
 * appetiser or a delivery fee should behave.
 */

import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import { parseAmount } from "../../lib/money";
import type { Member } from "../../lib/api";

export interface DraftLineItem {
  key: string;
  name: string;
  amount: string;
  assignees: number[];
}

interface Props {
  items: DraftLineItem[];
  onChange: (items: DraftLineItem[]) => void;
  members: Member[];
  currency: string;
}

let counter = 0;

export function blankLineItem(): DraftLineItem {
  counter += 1;
  return { key: `draft-${counter}`, name: "", amount: "", assignees: [] };
}

export function LineItemEditor({ items, onChange, members, currency }: Props) {
  const update = (key: string, patch: Partial<DraftLineItem>) =>
    onChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  const assigned = items.reduce(
    (sum, item) => sum + (item.assignees.length ? (parseAmount(item.amount, currency) ?? 0) : 0),
    0,
  );
  const shared = items.reduce(
    (sum, item) => sum + (item.assignees.length ? 0 : (parseAmount(item.amount, currency) ?? 0)),
    0,
  );

  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 ? (
        <p className="text-xs text-muted">
          Add each line off the receipt, then tap who shared it. Anything with nobody
          assigned is split as a group cost.
        </p>
      ) : null}

      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <li key={item.key} className="rounded border border-rule p-2">
            <div className="flex gap-2">
              <Input
                className="h-9 flex-1"
                placeholder="Item"
                aria-label="Line item name"
                value={item.name}
                onChange={(event) => update(item.key, { name: event.target.value })}
              />
              <Input
                className="h-9 w-24"
                inputMode="decimal"
                placeholder="0.00"
                aria-label="Line item amount"
                value={item.amount}
                onChange={(event) => update(item.key, { amount: event.target.value })}
              />
              <button
                type="button"
                aria-label={`Remove ${item.name || "line item"}`}
                className="tap px-2 text-muted hover:text-owing"
                onClick={() => onChange(items.filter((other) => other.key !== item.key))}
              >
                ✕
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {members.map((member) => {
                const on = item.assignees.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    aria-pressed={on}
                    className={cn("chip h-8 text-xs", on && "chip-on")}
                    onClick={() =>
                      update(item.key, {
                        assignees: on
                          ? item.assignees.filter((id) => id !== member.id)
                          : [...item.assignees, member.id],
                      })
                    }
                  >
                    {member.display_name}
                  </button>
                );
              })}
              {item.assignees.length === 0 ? (
                <span className="self-center text-2xs text-muted">Shared by everyone</span>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between text-xs text-muted">
        <button
          type="button"
          className="text-accent underline underline-offset-2"
          onClick={() => onChange([...items, blankLineItem()])}
        >
          + Add line item
        </button>
        <span className="num">
          {(assigned / 100).toFixed(2)} assigned · {(shared / 100).toFixed(2)} shared
        </span>
      </div>
    </div>
  );
}
